/**
 * ArticleRoleNext — 결정트리가 '완성품/부분품'을 물으며 멈췄을 때 카드에 그 질문을 보인다(2026-10-14).
 *   지도 화면은 호·소호 질문만 그려, 이 질문이 화면에 없어 '판별 사실 필요'에서 더 갈 수 없었다(실측: 첫 화면 멈춤 60건 중 22건).
 *   상품 모드에서는 서버가 article_role(법적 판단 키)을 관측 질문 obs_article_role("그대로 쓰는 완성품입니까, 다른 물품의
 *   부분품·부속품입니까?")로 바꿔 묻고, 답을 article_role 로 되돌려 쓴다(ProductFactDialogue.OBS_ROLE). 이 칸은 그 관측 질문을
 *   그대로 보이고 같은 키로 답한다. 미완성품(통칙 2(가))은 법적 판단이라 서버가 선택지에서 뺀다.
 *   추천은 표시만 한다 — '부분품' 추천 근거(먼저 맞는 것 하나):
 *     ① PROVE 소호 제안의 명칭  ② 계산 중인 호(가정·후보로 연 호)의 명칭  ③ PROVE 모델 제안 세번의 호 명칭  ④ 검색어의 부품 표현.
 *   답은 사용자가 누를 때만 저장한다(확인 게이트 유지). 2026-10-14 사용자 결정(A): 완성품/부분품은 상품 사실로 받는다.
 */
import { useHeadingTitles } from "./headingTitles";

export const OBS_ROLE = "obs_article_role";
const PART_WORDS = /부분품|부품|파츠|부속|\bparts?\b|\bassy\b|\bassembly\b|브래킷|bracket/i;
const PART_TITLE = /부분품|부속품/;

/** 지금 계산 중인 호 — 가정한 호(신호·소호 제안) → 후보로 연 호 순. */
export function routeHs4(tree) {
  return String(tree?.signal_assumption?.hs4 || tree?.candidate_route?.hs4 || "").slice(0, 4);
}

/**
 * 트리가 완성품/부분품에서 멈췄고 상품 대화가 관측 질문(obs_article_role)을 냈으면 질문·선택지·추천을 돌려준다. 아니면 null.
 *   heading = { hs4, title, from: "route" | "model" } — 근거로 쓸 호와 그 명칭.
 */
export function articleRoleQuestion(tree, query = "", heading = null) {
  if (!tree || tree.action !== "ask") return null;
  const asksRole = String(tree.reason || "") === "article_role_missing"
    || (Array.isArray(tree.questions) ? tree.questions : []).some((x) => x && x.fact_key === "article_role");
  const pfd = tree.product_fact_dialogue || {};
  const pool = [...(Array.isArray(pfd.questions) ? pfd.questions : []), ...(Array.isArray(pfd.planned_questions) ? pfd.planned_questions : [])];
  const q = pool.find((x) => x && x.fact_key === OBS_ROLE);
  if (!asksRole || !q || !Array.isArray(q.options) || !q.options.length) return null;
  const labels = q.option_labels || {};
  const options = q.options.map(String).filter((v) => v !== "undetermined" && v !== "unknown").map((v) => ({ value: v, label: labels[v] || v }));
  if (!options.length) return null;
  const proposal = tree.candidate_route?.subheading_proposal || null;
  let recommended = null;
  let basis = "";
  if (options.some((o) => o.value === "part")) {
    if (PART_TITLE.test(String(proposal?.label || ""))) {
      recommended = "part";
      basis = `PROVE 소호 제안 ${String(proposal.hs6 || "").replace(/^(\d{4})(\d{2})$/, "$1.$2")}의 명칭이 '부분품·부속품'입니다`;
    } else if (heading && PART_TITLE.test(String(heading.title || ""))) {
      recommended = "part";
      basis = heading.from === "model"
        ? `PROVE 모델 제안 세번의 제${heading.hs4}호 명칭이 '부분품·부속품'입니다`
        : `계산 중인 제${heading.hs4}호의 명칭이 '부분품·부속품'입니다`;
    } else if (PART_WORDS.test(String(query || ""))) {
      recommended = "part";
      basis = `검색어에 부품을 뜻하는 말('${String(query).match(PART_WORDS)[0]}')이 있습니다`;
    }
  }
  return {
    key: OBS_ROLE,
    prompt: q.prompt_ko || q.question || "이 물품은 그대로 쓰는 완성품입니까, 다른 물품의 부분품·부속품입니까?",
    hint: q.hint || "",
    options,
    recommended,
    basis,
  };
}

export default function ArticleRoleNext({ tree, query, modelHsk = "", onAnswer, disabled = false }) {
  // 계산 중인 호가 없으면(검색어 낱말로 류가 깨어난 경우) PROVE 모델 제안 세번의 호를 근거로 쓴다.
  const routed = routeHs4(tree);
  const modelHs4 = String(modelHsk || "").replace(/\D/g, "").slice(0, 4);
  const hs4 = routed || modelHs4;
  const titles = useHeadingTitles(hs4 ? [hs4] : []);
  const heading = hs4 ? { hs4, title: titles[hs4] || "", from: routed ? "route" : "model" } : null;
  const qn = articleRoleQuestion(tree, query, heading);
  if (!qn || !onAnswer) return null;
  return (
    <div data-testid="article-role-next" style={{ marginTop: 10, padding: "10px 12px", border: "1px solid var(--audit-border, #e5e7eb)", borderRadius: 8, fontSize: 12.5, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>다음 질문 · {qn.prompt}</div>
      {qn.hint && <div style={{ color: "var(--audit-text-muted)", marginBottom: 6 }}>{qn.hint}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {qn.options.map((o) => {
          const rec = qn.recommended === o.value;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onAnswer(qn.key, o.value)}
              style={{
                border: `1px solid ${rec ? "var(--audit-accent, #2563eb)" : "var(--audit-border, #e5e7eb)"}`,
                borderRadius: 999, padding: "2px 10px", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                background: "transparent", color: rec ? "var(--audit-accent, #2563eb)" : "var(--audit-text, #111)", fontWeight: rec ? 700 : 400,
              }}
            >
              {o.label}{rec ? " · 추천" : ""}
            </button>
          );
        })}
      </div>
      {qn.recommended && (
        <div style={{ marginTop: 6, color: "var(--audit-text-muted)" }}>
          추천 근거: {qn.basis} · 추천은 답이 아닙니다 — 물품이 실제로 무엇으로 제시되는지 확인한 뒤 눌러 주세요.
        </div>
      )}
    </div>
  );
}
